import { Pills } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

/** Same geometry as the status pill of a pillar tile, so both read as the same kind of badge. */
const PILL_PADDING = 'py-1.5';

interface UnalignedObjectivesPillProps {
    count: number;
    dataTestId: string;
}

/**
 * Objectives left without a pillar. Amber rather than a neutral count: it is the one case someone
 * may have to act on, and it is shown both inside the settings and, collapsed, in the header.
 */
export function UnalignedObjectivesPill({ count, dataTestId }: UnalignedObjectivesPillProps) {
    const { t } = useTranslation();

    return (
        <Pills
            variant={'warning'}
            status={'warning'}
            size={'sm'}
            className={PILL_PADDING}
            dataTestId={dataTestId}
        >
            {t('pillars.unaligned.count', { count })}
        </Pills>
    );
}

interface UnalignedObjectivesCountProps {
    /** Published objectives aligned with no pillar; `undefined` until the count is known. */
    count: number | undefined;
    hasError: boolean;
}

/**
 * How many objectives carry no strategic pillar, beside the setting that allows them.
 * A count that is still loading holds its place with a placeholder and a count that failed to load
 * says so: neither is ever drawn as a reassuring zero.
 */
export default function UnalignedObjectivesCount({
    count,
    hasError,
}: UnalignedObjectivesCountProps) {
    const { t } = useTranslation();

    if (hasError) {
        return (
            <span role={'alert'}>
                <Pills
                    variant={'inactiveLight'}
                    status={'inactive'}
                    size={'sm'}
                    className={PILL_PADDING}
                    dataTestId={'unaligned-objectives-error'}
                >
                    {t('pillars.unaligned.loadError')}
                </Pills>
            </span>
        );
    }

    if (count === undefined) {
        return (
            <span
                className={'bg-base-100 block h-[26px] w-[170px] animate-pulse rounded-full'}
                data-testid={'unaligned-objectives-loading'}
            />
        );
    }

    if (count === 0) {
        return (
            <Pills
                variant={'default'}
                size={'sm'}
                className={PILL_PADDING}
                dataTestId={'unaligned-objectives-count'}
            >
                {t('pillars.unaligned.none')}
            </Pills>
        );
    }

    return (
        <UnalignedObjectivesPill
            count={count}
            dataTestId={'unaligned-objectives-count'}
        />
    );
}
